/**
 * A ledger encrypted on this device — nothing readable at rest. The database
 * lives in memory (sql.js) and is written to IndexedDB only sealed: AES-256-GCM
 * under the ledger's key. The key is kept as a vault keeps it (tenantdb's
 * keyring): a root key wrapped by the passphrase and by a recovery key, and the
 * key wrapped by the root — under the ledger's org id, so the keyring is its
 * vault's if it moves to the cloud, and comes back down with it: the same
 * passphrase, the same recovery key. Boot asks for one of them before anything
 * opens (`renderUnlock` in boot.js).
 *
 *   IndexedDB okane/files `<blob>`  the ledger, sealed
 *   IndexedDB okane/files `<keys>`  { ring: { passphrase, recovery, keys } }
 *
 * (`sealedNames` in ledgers.js.) The first device to encrypt (2026-09-29) kept
 * `{ passphrase, recovery }` wrapping the key directly: still opened (`legacy`),
 * and moved to the cloud under a new keyring.
 */
import { crypto as c, newKeyring, openKeyring } from 'tenantdb/sync'
import { sealedNames } from './ledgers'

const LEGACY_AAD = { ledger: 'okane sealed ledger', passphrase: 'okane sealed passphrase', recovery: 'okane sealed recovery' }
const ledgerAad = (ledger) => (ledger.legacy ? LEGACY_AAD.ledger : `okane ledger ${ledger.id}`)

function files(mode, fn) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('okane', 1)
    req.onupgradeneeded = () => req.result.createObjectStore('files')
    req.onerror = () => reject(req.error)
    req.onsuccess = () => {
      const tx = req.result.transaction('files', mode)
      const out = fn(tx.objectStore('files'))
      tx.oncomplete = () => resolve(out?.result)
      tx.onerror = () => reject(tx.error)
    }
  })
}

/** The keys, remembered for this page once unlocked — `{ key, root, ring, legacy }`. */
let unlocked = null
/** The keyring as the cloud would keep it, with its root open — for a move up under it. Null for a legacy one. */
export const unlockedRing = () => (unlocked && !unlocked.legacy ? { root: unlocked.root, key: unlocked.key, wrapped: unlocked.ring } : null)

async function store(ledger, bytes, key, ring) {
  const { blob, keys } = sealedNames(ledger)
  const sealed = await c.seal(key, bytes, ledgerAad(ledger))
  await files('readwrite', (s) => {
    s.put(sealed, blob)
    s.put({ ring }, keys)
  })
}

/**
 * Seals `bytes` (a plain ledger file) as `ledger`, behind `passphrase` and a
 * new recovery key. Returns the recovery key's text.
 */
export async function createSealed(ledger, bytes, passphrase) {
  const ring = await newKeyring(ledger.id, passphrase)
  await store(ledger, bytes, ring.key, ring.wrapped)
  return ring.recovery
}

/** Seals `bytes` as `ledger` under a keyring it already has (a vault's, `{ wrapped, key }`). */
export async function createSealedFromRing(ledger, bytes, { wrapped, key }) {
  await store(ledger, bytes, key, wrapped)
}

/** Opens the ledger's keys with the passphrase or the recovery key; rejects `wrong-passphrase` / `wrong-recovery-key`. */
export async function unlock(ledger, { passphrase, recovery }) {
  const kept = await files('readonly', (s) => s.get(sealedNames(ledger).keys))
  if (!kept) throw Object.assign(new Error('There is no encrypted profile on this device.'), { code: 'no-sealed' })
  if (kept.ring) {
    const { root, key } = await openKeyring(ledger.id, kept.ring, { passphrase, recovery })
    unlocked = { key, root, ring: kept.ring, legacy: false }
  } else {
    const key = passphrase
      ? await c.unwrapWithPassphrase(kept.passphrase, passphrase, { aad: LEGACY_AAD.passphrase })
      : await c.unwrapWithRecovery(kept.recovery, c.parseRecoveryKey(recovery), { aad: LEGACY_AAD.recovery })
    unlocked = { key, root: null, ring: null, legacy: true }
  }
  return unlocked.key
}

/** The database driver over the sealed ledger, once unlocked. */
export async function openSealedDriver(ledger) {
  if (!unlocked) throw new Error('The encrypted profile is locked.')
  const { createDriver } = await import('../db/drivers/sqljs.js')
  const key = unlocked.key
  const aad = ledgerAad(ledger)
  return createDriver({
    file: sealedNames(ledger).blob,
    name: 'sealed',
    seal: (bytes) => c.seal(key, bytes, aad),
    unseal: (stored) => c.open(key, stored, aad),
  })
}

/** Deletes an encrypted ledger and its keys from this device (`names`: `sealedNames`). */
export async function destroySealed({ blob, keys }) {
  await files('readwrite', (s) => {
    s.delete(blob)
    s.delete(keys)
  })
}
